import React from 'react'

import { useEffect, useMemo, memo, useCallback, useState, useRef } from 'react'

import { RxCrossCircled } from 'react-icons/rx'

import { v4 as uniqid } from 'uuid'

import { HiOutlineExclamation } from 'react-icons/hi'
import { IoIosArrowDown }       from 'react-icons/io'

import onMultiSelectKeyDown from './Toolkit/onMultiSelectKeyDown'
import PickerPickedFetcher  from './Toolkit/PickerPickedFetcher'
import NoPickableResult     from './Toolkit/NoPickableResult'
import PickerFetcher        from './Toolkit/PickerFetcher'
import PickableItem         from './Toolkit/PickableItem'

import Loader from '@Pure/Loader'

import nodeIsOrBelongsTo from '@function/nodeIsOrBelongsTo'
import arePropsEqual     from '@function/arePropsEqual'
import bemit             from '@function/bemit'

import { isTrue } from '@root/utility'

import FormsStore from '@Store/Forms'

const PREFIX = '##pickable-';

const test_options = Array.from({ length: 40 })
.map((_v, i) => ({ value: String(i), label: 'test ' + (i+1) }));

test_options[4].disabled = true;

export default memo(function ModelPicker ({ onChange, ...props })
{
	const ref = useRef();
	const input_ref = useRef();
	const toggle_ref = useRef();
	const value_ref = useRef();
	const search_timeout = useRef();

	const [id, setId] = useState(uniqid());
	const [toggle_id, setToggleId] = useState(uniqid());
	const [toggle_label_id, setToggleLabelId] = useState(uniqid());

	const [fetch_error, setFetchError] = useState();
	const [options, setOptions] = useState();
	const [is__visible, setVisibility] = useState(false);
	const [picked_id, setPickedId] = useState();
	const [dirty, setDirty] = useState(false);
	const [loading, setLoading] = useState();
	const [searched, setSearched] = useState();

	const [reset_id, setResetId] = useState();

	const state_value = FormsStore
	.useState(s => s[props.form || 'no-form']?.[props.name]);

	const FORM_NAME = props['form'] || 'no-form';

	const type = typeof onChange === 'function'
	? 'legacy'
	: 'pullstate';

	const is__disabled = isTrue(props['read-only']);

	useEffect(() =>
	{
		return function clearTimeout ()
		{
			if (search_timeout?.current)
			{
				window.clearTimeout(search_timeout.current);
			}
		}
	},
	[]);

	useEffect(() =>
	{
		if (type !== 'legacy') return;

		if (props.value && !value_ref.current)
		{
			setLoading('id-fetch');
			setPickedId(props.value);
		}
	},
	[type, props.value]);

	const modifiers = useMemo(function ()
	{
		const modifiers = (props.bemit || []).slice(0);

		if (fetch_error?.error || props.error) modifiers.push('error');

		if (is__visible) modifiers.push('open');
		if (is__disabled) modifiers.push('disabled');

		return modifiers;
	},
	[props.error, fetch_error, props.bemit, is__visible, is__disabled]);

	const input_bemit = props['input-bemit'];

	const input_modifiers = useMemo(function ()
	{
		const modifiers = ['model-picker'];

		if (fetch_error?.error || props.error) modifiers.push('error');
		if (is__disabled) modifiers.push('disabled');
		if (props.required) modifiers.push('required');

		return Array.isArray(input_bemit)
		? input_bemit.concat(modifiers)
		: modifiers;
	},
	[props.error, fetch_error, is__disabled, props.required, input_bemit]);

// ferme les options quand on click en dehors
	useEffect(() =>
	{
		if (is__visible === false) return;

		document.addEventListener('click', listener);

		return () => document.removeEventListener('click', listener);

		function listener (e)
		{
		// ignore le click s'il est dans le picker
			if (nodeIsOrBelongsTo(e?.target, ref?.current)) return;

			setVisibility(false);
		}
	},
	[is__visible]);

	const handleKeyDown = useCallback (function (e)
	{
		if (is__disabled) return;

		onMultiSelectKeyDown(
		{
			toggle_id, toggle_ref, e, setVisibility, PREFIX
		});
	},
	[toggle_id, toggle_ref, is__disabled]);

	useEffect(function ()
	{
		if (type !== 'pullstate') return;

		if (state_value === undefined) // reset
		{
			input_ref.current.value = '';
			setPickedId(undefined);

			return () => void 0;
		}

		if (state_value === value_ref.current) return;

		if (state_value) setLoading('id-fetch');

		setPickedId(state_value);
	},
	[type, state_value]);

	useEffect(function ()
	{
		setLoading(false);
	},
	[fetch_error]);

	useEffect(function ()
	{
		if (!Array.isArray(props.exclude) || !props.exclude.length) return;

		setOptions(function (options)
		{
			return options
			.filter(opt => props.exclude.includes(opt.value) === false);
		});
	},
	[props.exclude]);

	const LabelLeftIcon = props['label-left-icon'];

	const pickable_items = (options || []).map(function (opt, i)
	{
		const value = opt.value || '';

		const label = typeof opt.label === 'undefined'
		? value
		: opt.label + (opt.suffix || '');

		return (
			<PickableItem key={ value } value={ value } label={ label }
				disabled={ is__disabled || isTrue(opt.disabled) }
				onClick={ setPickedOption }
				requestClose={ () => setVisibility(false) }
				toggle-id={ toggle_id }
				index={ i }
				onKeyDown={ handleKeyDown }
				form={ FORM_NAME }
				name={ props.name }
				prefix={ PREFIX }
				data={ opt.data }
				colored={ opt.colored === true }
			/>
		);
	});

	const handleFocus = useCallback(function (e)
	{
		setVisibility(true);
	},
	[]);

	const clear = useCallback(function (e)
	{
		if (search_timeout?.current)
		{
			window.clearTimeout(search_timeout.current);
			search_timeout.current = undefined;
		}

		input_ref.current.value = '';

		setSearched('');
		setResetId(uniqid());

		if (type === 'pullstate')
		{
			FormsStore.update(function (state)
			{
				state[FORM_NAME] = state[FORM_NAME] || { };

				state[FORM_NAME][props.name] = '';
			});
		}
		else
		{
			onChange({ e, name: props.name, value: '' });
		}
	},
	[type]);

	const displayed_options = !options
	? undefined
	: pickable_items.length
	? pickable_items
	: input_ref.current?.value && !loading
	? <NoPickableResult />
	: !input_ref.current?.value && !loading
	? <NoPickableResult empty />
	: undefined;

	return (
		<div className={ bemit ('c-model-picker', modifiers) } ref={ ref }
			id={ toggle_id }>

			<div className={ bemit('c-input', input_modifiers) }>

				{
					props.label ?
					(
						<label htmlFor={ props.id || id }
							className="c-input__label">
							{
								LabelLeftIcon
								? <LabelLeftIcon className="c-input__label-icon" />
								: undefined
							}
							{ props.label }
						</label>
					)
					: undefined
				}

				<div className="c-input__input-wrapper">
					<input type={ props.type } className="c-input__input"
						id={ props.id || id }
						onChange={ handleInputChange }
						name={ props.name }
						disabled={ props['read-only'] === true }
						placeholder={ props.placeholder }
						autoComplete={ props.autoComplete || 'off' }
						onKeyDown={ handleKeyDown }
						onFocus={ handleFocus }
						ref={ input_ref }
						spellCheck="false"
					/>

					<span className="c-input__border" />

					<button type="button" className="c-model-picker__toggle"
						disabled={ is__disabled }
						aria-labelledby={ toggle_label_id }
						onClick={ e => setVisibility(v => !v) }
						onKeyDown={ handleKeyDown }
						ref={ toggle_ref }>
						<span className="u-readers-only" id={ toggle_label_id }>
							Choisir une option
							{ props.label ? ' ' : undefined }
							{ props.label ? '(' + props.label + ')' : undefined }
						</span>
						<IoIosArrowDown className="c-model-picker__toggle-icon" />
					</button>

					{
						loading ?
						(
							<Loader className="c-loader"
								bemit={ ['primary', 'bg-input', 'model-picker'] }
							/>
						)
						: undefined
					}

					<ul className="c-model-picker__options"
						aria-hidden={ !is__visible || !displayed_options }>
						{ displayed_options }
					</ul>

					{
						props['resetable'] ?
						(
							<button type="button" className="c-input__clear"
								onClick={ clear }>
								<RxCrossCircled className="c-input__clear-icon" />
								<span className="u-readers-only">Effacer</span>
							</button>
						)
						: undefined
					}

				</div>

			</div>

			{
				props.error || fetch_error?.error ?
				(
					<span className={ bemit('c-field-error', ['input']) }>
						<HiOutlineExclamation className="c-field-error__icon" />
						{ props.error || fetch_error?.error }
					</span>
				)
				: undefined
			}

			<PickerFetcher onError={ setFetchError }
				onResults={ setOptions }
				setVisibility={ setVisibility }
				setLoading={ setLoading }
				url={ props.url }
				model={ props.model }
				custom={ props.custom }
				exclude={ props.exclude }
				cancel={ loading === 'id-fetch' }
				searched={ searched }
				no-initial-fetch={ props.value !== undefined && !reset_id }
				key={ reset_id }
			/>

			<PickerPickedFetcher onError={ setFetchError }
				onPicked={ setPickedOption }
				onResults={ setOptions }
				url={ props.url }
				model={ props.model }
				picked={ picked_id }
			/>

		</div>
	);

	function handleInputChange (e)
	{
		setLoading('searching');

		if (search_timeout?.current)
		{
			window.clearTimeout(search_timeout.current);
		}

		search_timeout.current = window.setTimeout(function ()
		{
			if (!input_ref?.current) return; // input plus présent

			search_timeout.current = undefined;

			if (input_ref.current.value !== searched)
			{
				setSearched(input_ref.current.value);
				setLoading('searching');
			}
			else
			{
				setLoading(false);
			}
		},
		600);

		if (!dirty) setDirty(true);

		if (type === 'pullstate')
		{
			FormsStore.update(function (state)
			{
				state[FORM_NAME] = state[FORM_NAME] || { };

				state[FORM_NAME][props.name] = '';
			});
		}
		else
		{
			onChange({ e, name: props.name, value: '' });
		}
	}

	function setPickedOption (e, value, data)
	{
		if (!value) return;

		const id = typeof value === 'string'
		? value
		: value.value;

		value_ref.current = id;

		input_ref.current.value = value === id
		? (options || []).find(opt => opt.value === value)?.label || ''
		: value?.label || '';

		toggle_ref.current?.focus?.();

		setVisibility(false);

		if (type === 'pullstate')
		{
			FormsStore.update(function (state)
			{
				state[FORM_NAME] = state[FORM_NAME] || { };

				state[FORM_NAME][props.name] = id;
			});
		}
		else
		{
			onChange({ e, name: props.name, value: id, data: data });
		}

		if (loading) setLoading(false);

		if (dirty) setDirty(false);
	}
},
arePropsEqual);